import { TokenManager } from "@/supabase/supabaseClient";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

export interface UserInvite {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  role_id?: string;
  invited_by?: string;
  auth_user_id?: string;
  status: "pending" | "accepted" | "expired" | "cancelled";
  invited_at: string;
  accepted_at?: string;
  inviter?: {
    email: string;
    first_name?: string;
    last_name?: string;
  };
}

export interface SendInviteData {
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  roleId?: string;
}

export interface InviteResponse {
  success: boolean;
  message?: string;
  invite?: {
    id: string;
    email: string;
    status: string;
  };
  error?: string;
}

export class InviteService {
  /**
   * Get auth token for API calls - ensures token is fresh
   */
  private static async getAuthToken(): Promise<string | null> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.error("[InviteService] Token not ready");
        return null;
      }

      const token = TokenManager.getStoredToken();
      if (!token) {
        console.error("[InviteService] No token in storage");
        return null;
      }

      // Log token info for debugging (first 20 chars only)
      console.log(
        `[InviteService] Token available: ${token.substring(0, 20)}...`
      );
      return token;
    } catch (error) {
      console.error("[InviteService] Error getting token:", error);
      return null;
    }
  }

  /**
   * Send an invitation email to a new user
   */
  static async sendInvite(data: SendInviteData): Promise<InviteResponse> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { success: false, error: "Authentication token not available" };
      }

      const response = await fetch(`${apiBaseUrl}/api/invite/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || result.message || "Failed to send invitation",
        };
      }

      return result;
    } catch (error) {
      console.error("Error sending invite:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to send invitation",
      };
    }
  }

  /**
   * Get all invitations (admin only)
   */
  static async getInvites(): Promise<{
    invites: UserInvite[];
    error?: string;
  }> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { invites: [], error: "Authentication token not available" };
      }

      const response = await fetch(`${apiBaseUrl}/api/invite/list`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          invites: [],
          error: error.error || "Failed to fetch invitations",
        };
      }

      const result = await response.json();
      return { invites: result.invites || [] };
    } catch (error) {
      console.error("Error fetching invites:", error);
      return {
        invites: [],
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch invitations",
      };
    }
  }

  /**
   * Resend an invitation email
   */
  static async resendInvite(
    inviteId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { success: false, error: "Authentication token not available" };
      }

      const response = await fetch(
        `${apiBaseUrl}/api/invite/resend/${inviteId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || "Failed to resend invitation",
        };
      }

      return { success: true };
    } catch (error) {
      console.error("Error resending invite:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to resend invitation",
      };
    }
  }

  /**
   * Delete/cancel an invitation
   */
  static async deleteInvite(
    inviteId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { success: false, error: "Authentication token not available" };
      }

      const response = await fetch(`${apiBaseUrl}/api/invite/${inviteId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || "Failed to delete invitation",
        };
      }

      return { success: true };
    } catch (error) {
      console.error("Error deleting invite:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to delete invitation",
      };
    }
  }
}
